import { z } from "zod";

import { contrastRatio } from "./mark-color.ts";

/**
 * The brand pack: what a business changes in the look of the app (mobile V2 and white-label spec,
 * slice 2.1, in ai-personal-assistant `teamna/strategy/mobile-v2-and-white-label.md`).
 *
 * The native build carries only what the stores need (name, icon, splash, bundle id). Everything in
 * this pack comes from the server at runtime, so a business can change it with no new build.
 *
 * - `accentHex: null` is the Teamna look. The apps then use the hand-tuned Teamna palette exactly.
 * - `tokens` and `backgrounds` are slots for the branded app (slice 3.4). Only the defaults exist
 *   today, and the apps do not draw them yet.
 * - Labels are not here: they already come on each business row (`labels`), and every role reads them.
 */

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, "A colour is #rrggbb.");

/** The screens a background image can cover (slice 3.4). `all` is every screen with no own image. */
export const brandBackgroundScreens = ["all", "home", "child_page", "sign_in"] as const;
export type BrandBackgroundScreen = (typeof brandBackgroundScreens)[number];

/** Design tokens for the branded app (slice 3.4). Each one picks from tested options only. */
export const brandTokensSchema = z.object({
  buttonStyle: z.enum(["filled", "outline"]).default("filled"),
  corners: z.enum(["square", "round", "pill"]).default("round"),
  secondaryHex: hexColor.nullable().default(null),
  surfaceTint: z.boolean().default(false),
});
export type BrandTokens = z.infer<typeof brandTokensSchema>;

export const brandBackgroundSchema = z.object({
  screen: z.enum(brandBackgroundScreens),
  imageUrl: z.string().url(),
});
export type BrandBackground = z.infer<typeof brandBackgroundSchema>;

export const brandPackSchema = z.object({
  /** The business accent, or null for the Teamna look. */
  accentHex: hexColor.nullable(),
  /** The business logo (`/brand/:businessId/logos/:logoId/{full,mark}.png`), or null. */
  logo: z.object({ businessId: z.string().uuid(), logoId: z.string().uuid() }).nullable(),
  supportUrl: z.string().url().nullable(),
  privacyUrl: z.string().url().nullable(),
  tokens: brandTokensSchema.default({}),
  backgrounds: z.array(brandBackgroundSchema).max(8).default([]),
});
export type BrandPack = z.infer<typeof brandPackSchema>;

/** The Teamna look. Every app uses it until a business pack arrives, and when a pack fails to load. */
export const TEAMNA_BRAND_PACK: BrandPack = {
  accentHex: null,
  logo: null,
  supportUrl: null,
  privacyUrl: null,
  tokens: { buttonStyle: "filled", corners: "round", secondaryHex: null, surfaceTint: false },
  backgrounds: [],
};

/** The accent roles of one theme (light or dark). The apps keep their other colours as they are. */
export type AccentRoles = {
  accent: string;
  accentStrong: string;
  accentSoft: string;
  accentSurface: string;
  accentOnAccent: string;
  /** The border of a pressed primary button. */
  accentPressed: string;
};

/**
 * Teamna's own accent roles: the hand-tuned blue of the mobile `lightColors` and `darkColors`. Here
 * so Desk can draw the Teamna look in the app colour preview (slice 2.2). A test keeps the mobile
 * tokens and these values the same.
 */
export const TEAMNA_ACCENT_ROLES: { light: AccentRoles; dark: AccentRoles } = {
  light: {
    accent: "#326CC5",
    accentStrong: "#1C449D",
    accentSoft: "#6FB0F5",
    accentSurface: "#E7F2FC",
    accentOnAccent: "#FFFFFF",
    accentPressed: "#15367C",
  },
  dark: {
    accent: "#6FB0F5",
    accentStrong: "#9ECAF5",
    accentSoft: "#4895ED",
    accentSurface: "#14294A",
    accentOnAccent: "#06183A",
    accentPressed: "#15367C",
  },
};

/** The text colours of the mobile themes, for previews of the app on Desk. */
export const BRAND_TEXT = {
  light: { primary: "#111A2B", border: "#E0E6EE" },
  dark: { primary: "#E8EEF8", border: "#243048" },
} as const;

/** The surfaces the accent must read on. The same values as the mobile and web themes. */
export const BRAND_SURFACES = {
  light: { surface: "#FFFFFF", background: "#F6F8FB" },
  dark: { surface: "#121B2C", background: "#0B1220" },
} as const;

/** WCAG AA for normal text. */
export const WCAG_AA = 4.5;

function channels(hex: string): [number, number, number] {
  const value = Number.parseInt(hex.slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function toHex([r, g, b]: [number, number, number]): string {
  return `#${[r, g, b].map((part) => Math.round(Math.min(255, Math.max(0, part))).toString(16).padStart(2, "0")).join("")}`.toUpperCase();
}

/** `amount` of `b` mixed into `a` (0 keeps `a`, 1 gives `b`). */
export function mixHex(a: string, b: string, amount: number): string {
  const x = channels(a);
  const y = channels(b);
  return toHex([0, 1, 2].map((i) => x[i]! + (y[i]! - x[i]!) * amount) as [number, number, number]);
}

/** Mixes `toward` into `hex` step by step until `test` passes. Gives `toward` itself at the end. */
function stepUntil(hex: string, toward: string, test: (candidate: string) => boolean): string {
  for (let amount = 0; amount <= 1.0001; amount += 0.04) {
    const candidate = mixHex(hex, toward, amount);
    if (test(candidate)) return candidate;
  }
  return toward.toUpperCase();
}

/**
 * The tinted surface behind accent text (badges, a pressed ghost button): `start` of `toward` mixed
 * into the accent, then more, until the accent reads on it at AA. `toward` is white in light mode and
 * the dark background in dark mode, and the accent already passes on both, so the loop always ends.
 */
function surfaceFor(accent: string, toward: string, start: number): string {
  for (let amount = start; amount <= 1.0001; amount += 0.02) {
    const candidate = mixHex(accent, toward, Math.min(1, amount));
    if (contrastRatio(accent, candidate) >= WCAG_AA) return candidate;
  }
  return toward.toUpperCase();
}

/**
 * The accent roles for a business colour, in light and dark, each checked for WCAG AA:
 *
 * - light `accent` takes white text at 4.5:1 or more, and reads as text on white at 4.5:1 or more.
 *   A colour that is too light is made darker until both pass.
 * - dark `accent` reads as text on the dark surface at 4.5:1 or more (made lighter if not), and
 *   `accentOnAccent` (the label of a primary button) is the dark background or white, whichever
 *   gives more contrast on it.
 * - `surface` is a tint the accent itself reads on at AA (see `surfaceFor`).
 * - `strong`, `soft` and `pressed` are mixes of the accent with black, white or the surface.
 *
 * Teamna's own palette is hand-tuned and does not come from here: `accentHex: null` keeps it.
 */
export function paletteFromAccent(hex: string): { light: AccentRoles; dark: AccentRoles } {
  const base = hex.toUpperCase();
  const lightAccent = stepUntil(base, "#000000", (c) => contrastRatio(c, "#FFFFFF") >= WCAG_AA && contrastRatio(c, BRAND_SURFACES.light.background) >= WCAG_AA);
  const lightStrong = mixHex(lightAccent, "#000000", 0.3);
  const light: AccentRoles = {
    accent: lightAccent,
    accentStrong: lightStrong,
    accentSoft: mixHex(lightAccent, "#FFFFFF", 0.4),
    // A tint of the accent that the accent (and the darker strong role) still reads on at AA.
    accentSurface: surfaceFor(lightAccent, "#FFFFFF", 0.9),
    accentOnAccent: "#FFFFFF",
    accentPressed: mixHex(lightStrong, "#000000", 0.25),
  };

  const darkAccent = stepUntil(base, "#FFFFFF", (c) => contrastRatio(c, BRAND_SURFACES.dark.surface) >= WCAG_AA && contrastRatio(c, BRAND_SURFACES.dark.background) >= WCAG_AA);
  const onDark = contrastRatio(BRAND_SURFACES.dark.background, darkAccent) >= contrastRatio("#FFFFFF", darkAccent) ? BRAND_SURFACES.dark.background : "#FFFFFF";
  const dark: AccentRoles = {
    accent: darkAccent,
    accentStrong: mixHex(darkAccent, "#FFFFFF", 0.4),
    accentSoft: mixHex(darkAccent, BRAND_SURFACES.dark.surface, 0.25),
    accentSurface: surfaceFor(darkAccent, BRAND_SURFACES.dark.background, 0.8),
    accentOnAccent: onDark,
    accentPressed: mixHex(darkAccent, "#FFFFFF", 0.55),
  };
  return { light, dark };
}

/** True when white text on the colour, and the colour as text on white, both pass AA. */
export function accentPassesAA(hex: string): boolean {
  return contrastRatio(hex, "#FFFFFF") >= WCAG_AA && contrastRatio(hex, BRAND_SURFACES.light.background) >= WCAG_AA;
}
